import "../../styles/import-person.scss";
import { useRef, useState, useEffect } from "react";
import PageLayout from "../../components/page-layout";
import actions from "../../actions/actions";
import Card from "../../components/card";
import ExpandPanel from "../../components/expand-panel";
import { GuiButton } from "../../components/global-ui/gui-button";
import { Icon } from "../../components/icon";
import navigate from "../../utilities/navigation";
import { routerHistory } from "../../redux/redux-config";
import * as XLSX from "xlsx";
import {
  Path,
  IMPORT_USERS_ENABLED,
  ACCEPTED_FILE_TYPES,
  IMPORT_TEMPLATE_PATH,
  IMPORT_TEMPLATE_FILENAME,
  IMPORT_TEMPLATE_HEADERS,
} from "../../utilities/constants";
import { showToast } from "../../components/toast";
import ImportStepper from "../../components/import-stepper";
import ImportSettings from "./ImportSettings";
import ImportDataMapping from "./ImportDataMapping";
import LoadingSpinner from "../../components/loading-spinner";

export default function ImportPersonPage() {
  useEffect(() => {
    if (!IMPORT_USERS_ENABLED) routerHistory.replace(Path.People);
  }, []);

  return IMPORT_USERS_ENABLED ? <ImportPersonForm /> : null;
}

function ImportPersonForm() {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [importSettings, setImportSettings] = useState({
    userType: null,
    userRecordMode: "doNotDelete",
    contactInfoMode: "doNotDelete",
  });
  const [userLevels, setUserLevels] = useState({
    Level1: "Student",
    Level3: "Faculty",
  });

  useEffect(() => {
    async function fetchLevels() {
      let result = await actions.people.getImportUserLevels();
      if (result?.success && result.levels) {
        setUserLevels({
          Level1: result.levels.level1 || "Student",
          Level3: result.levels.level3 || "Faculty",
        });
      }
    }
    fetchLevels();
  }, []);

  function handleDownloadTemplate(e) {
    e.preventDefault();
    const link = document.createElement("a");
    link.href = IMPORT_TEMPLATE_PATH;
    link.download = IMPORT_TEMPLATE_FILENAME;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function handleSettingsChange(settings) {
    setImportSettings(settings);
  }

  function validateFileType(file) {
    if (!file) return "File does not meet requirements. Please try again.";

    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith(".xls") && !fileName.endsWith(".xlsx")) {
      return "File does not meet requirements. Please try again.";
    }

    return "";
  }

  function validateFileHeaders(file) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
          const headers = (rows[0] || []).map((h) => String(h).trim());

          const templateHeaders = IMPORT_TEMPLATE_HEADERS.map((h) => h.trim());
          const matches = templateHeaders.every((col) => headers.includes(col));

          if (!matches) {
            resolve("File does not meet requirements. Please try again.");
          } else {
            resolve("");
          }
        } catch {
          resolve("File does not meet requirements. Please try again.");
        }
      };
      reader.onerror = () => {
        resolve("File does not meet requirements. Please try again.");
      };
      reader.readAsArrayBuffer(file);
    });
  }

  async function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (file) {
      const typeError = validateFileType(file);
      if (typeError) {
        setFileError(typeError);
        setSelectedFile(null);
        return;
      }
      const headerError = await validateFileHeaders(file);
      setFileError(headerError);
      setSelectedFile(headerError ? null : file);
    }
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }

  function handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }

  async function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      const typeError = validateFileType(file);
      if (typeError) {
        setFileError(typeError);
        setSelectedFile(null);
        return;
      }
      const headerError = await validateFileHeaders(file);
      setFileError(headerError);
      setSelectedFile(headerError ? null : file);
    }
  }

  function handleBrowseClick() {
    fileInputRef.current?.click();
  }

  function handleNextStep() {
    if (currentStep === 1 && selectedFile && !fileError && !isUploading) {
      setCurrentStep(2);
    } else if (currentStep === 2 && importSettings.userType) {
      setCurrentStep(3);
    }
  }

  function handlePrevStep() {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }

  async function handleImport(mappings) {
    if (!selectedFile) return;
    setIsUploading(true);
    try {
      const result = await actions.people.importUsers(
        selectedFile,
        importSettings,
        mappings,
      );
      if (result && result?.success) {
        showToast("success", "Users imported successfully!");
        navigate(Path.People);
      } else {
        setIsUploading(false);
      }
    } catch {
      showToast("error", "An error occurred while importing users.");
      setIsUploading(false);
    }
  }

  return (
    <PageLayout
      title="Import Users"
      className="import-person"
      backButton={true}
      onBackClick={() => {
        navigate(Path.People);
      }}
      backButtonLabel="Back"
    >
      {isUploading && (
        <div className="import-loader-overlay">
          <LoadingSpinner show={true} />
        </div>
      )}
      <Card>
        <ImportStepper currentStep={currentStep} />

        {currentStep === 1 && (
          <ExpandPanel title="Upload File" expanded={true}>
            <p className="upload-description">
              Upload an Excel file (.xls or .xlsx) to bulk import user data and
              map their data to existing fields.
            </p>

            <div className="upload-notes">
              <div className="upload-notes-header">
                <i className="fas fa-info-circle"></i>
                <span>Notes:</span>
              </div>
              <ul className="upload-notes-list">
                <li>To update existing users, include Unique ID field.</li>
                <li>
                  Values in the Primary Email field will be used for account
                  authentication.
                </li>
                <li>
                  Not sure what to include?{" "}
                  <strong>
                    Download the{" "}
                    <a
                      href={IMPORT_TEMPLATE_PATH}
                      className="csv-template-link"
                      onClick={handleDownloadTemplate}
                    >
                      Excel template
                    </a>
                    . To ensure account mapping, do not modify the column
                    headers.
                  </strong>
                </li>
              </ul>
            </div>

            {/* Drag and Drop Zone */}
            <div
              className={`file-drop-zone ${isDragging ? "dragging" : ""} ${
                selectedFile ? "has-file" : ""
              } ${fileError ? "has-error" : ""}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_FILE_TYPES}
                onChange={handleFileSelect}
                className="file-input-hidden"
              />

              {selectedFile ? (
                <div className="file-success">
                  <div className="file-success-info">
                    <i className="fas fa-check-circle file-success-icon"></i>
                    <span className="file-success-text">
                      File Uploaded: <strong>{selectedFile.name}</strong>
                    </span>
                  </div>
                  <GuiButton
                    className="replace-file-btn"
                    onClick={handleBrowseClick}
                  >
                    Replace with another file
                  </GuiButton>
                </div>
              ) : (
                <div className="drop-zone-content">
                  {fileError && (
                    <div className="file-error-inline">
                      <i className="fas fa-circle-xmark file-error-icon"></i>
                      <span className="file-error-text">{fileError}</span>
                    </div>
                  )}
                  <Icon icon="cloud-arrow-up" weight="400" />
                  <p className="drop-zone-text">
                    <strong>Drag and drop your file here</strong>
                  </p>
                  <p className="drop-zone-hint">or</p>
                  <GuiButton
                    className="choose-file-btn"
                    onClick={handleBrowseClick}
                  >
                    <Icon icon="upload" weight="400" />
                    Choose File
                  </GuiButton>
                </div>
              )}
            </div>

            <div className="next-button-bar">
              <div
                className={`step-btn ${!selectedFile || !!fileError || isUploading ? "disabled" : ""}`}
                onClick={handleNextStep}
              >
                <span>Next</span>
                <i className="fa fa-chevron-right"></i>
              </div>
            </div>
          </ExpandPanel>
        )}

        {currentStep === 2 && (
          <ImportSettings
            file={selectedFile}
            onBack={handlePrevStep}
            onNext={handleNextStep}
            importSettings={importSettings}
            onSettingsChange={handleSettingsChange}
            levels={userLevels}
          />
        )}

        {currentStep === 3 && (
          <ImportDataMapping
            file={selectedFile}
            onBack={handlePrevStep}
            onImport={handleImport}
          />
        )}
      </Card>
    </PageLayout>
  );
}
